import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, EyeOff, Lock, Globe } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { articlesAPI } from '../../lib/api';

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchArticles = async () => {
    try {
      const response = await articlesAPI.getAll();
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const handleDelete = async (id) => {
    try {
      await articlesAPI.delete(id);
      toast.success('Article deleted successfully');
      fetchArticles();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const togglePublished = async (article) => {
    try {
      await articlesAPI.update(article.id, { published: !article.published });
      toast.success(article.published ? 'Article unpublished' : 'Article published');
      fetchArticles();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
            Articles
          </h1>
          <p className="text-muted-foreground">
            Manage heritage articles and stories.
          </p>
        </div>
        <Link to="/admin/articles/new">
          <Button data-testid="new-article-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Article
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-background p-4 border border-border animate-pulse">
              <div className="h-6 bg-muted w-1/3 mb-2"></div>
              <div className="h-4 bg-muted w-1/2"></div>
            </div>
          ))}
        </div>
      ) : articles.length > 0 ? (
        <div className="bg-background border border-border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium text-foreground">Title</th>
                  <th className="text-left p-4 font-medium text-foreground">Category</th>
                  <th className="text-left p-4 font-medium text-foreground">Visibility</th>
                  <th className="text-left p-4 font-medium text-foreground">Status</th>
                  <th className="text-left p-4 font-medium text-foreground">Date</th>
                  <th className="text-right p-4 font-medium text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr key={article.id} className="border-t border-border">
                    <td className="p-4">
                      <Link to={`/articles/${article.id}`} className="text-foreground hover:text-accent font-medium">
                        {article.title}
                      </Link>
                    </td>
                    <td className="p-4">
                      <span className="tag-badge">{article.category}</span>
                    </td>
                    <td className="p-4">
                      <span className={`flex items-center gap-1 text-sm ${article.visibility === 'restricted' ? 'text-secondary' : 'text-green-600'}`}>
                        {article.visibility === 'restricted' ? (
                          <><Lock className="h-4 w-4" /> Restricted</>
                        ) : (
                          <><Globe className="h-4 w-4" /> Public</>
                        )}
                      </span>
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => togglePublished(article)}
                        className={`flex items-center gap-1 text-sm ${article.published ? 'text-green-600' : 'text-muted-foreground'}`}
                      >
                        {article.published ? (
                          <><Eye className="h-4 w-4" /> Published</>
                        ) : (
                          <><EyeOff className="h-4 w-4" /> Draft</>
                        )}
                      </button>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {new Date(article.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/articles/${article.id}/edit`}>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Article</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{article.title}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(article.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-background border border-border p-12 text-center">
          <h3 className="font-heading text-xl text-foreground mb-2">No Articles Yet</h3>
          <p className="text-muted-foreground mb-6">Create your first heritage article.</p>
          <Link to="/admin/articles/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Article
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
